/**
 * Card view for one rehab project. Mirrors the rehab.html design:
 * thumbnail + address head, progress + days tiles, budget bar, and an
 * inline primary action ("Start Rehab" / "Mark Complete") whose label
 * depends on the project's derived status.
 */
import { useState } from "react";
import { Check, ChevronRight, Home, Pencil, Play } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useRentals } from "@/hooks/use-rentals";
import type { RehabProject } from "@/hooks/use-rehab";

import { StrategyBadge } from "@/components/pipeline/StrategyBadge";

import {
  daysBetween,
  deriveBudget,
  deriveProgressPct,
  deriveStatus,
  STATUS_LABEL,
  type RehabStatus,
} from "./rehab-status";

function formatCurrency(v: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(v);
}

interface RehabProjectCardProps {
  project: RehabProject;
  onOpen: (project: RehabProject) => void;
  /** "Start Rehab" — begins a not-started project or restarts a parked one. */
  onStart: (id: string) => void;
  onComplete: (id: string) => void | Promise<void>;
}

const STATUS_STYLES: Record<RehabStatus, string> = {
  not_started: "bg-muted text-muted-foreground border border-border",
  in_progress: "bg-warning/15 text-warning",
  completed: "bg-success/15 text-success",
};

export function RehabProjectCard({
  project,
  onOpen,
  onStart,
  onComplete,
}: RehabProjectCardProps) {
  const { listings } = useRentals();
  const [rentedBlockOpen, setRentedBlockOpen] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  // A property can't go into rehab while it's actively rented. Other listing
  // statuses (active/paused/draft/archived) — or no listing at all — don't block.
  const isRented = listings.some(
    (l) =>
      l.pipeline_property_id === project.pipeline_property_id &&
      l.status === "rented",
  );

  const handleStart = () => {
    if (isRented) {
      setRentedBlockOpen(true);
      return;
    }
    onStart(project.id);
  };

  const pd = (project.property_data ?? {}) as {
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    imageUrl?: string;
    strategy?: string;
  };
  const addr1 = pd.address ?? project.property_address ?? "Property";
  const addr2 =
    [pd.city, pd.state].filter(Boolean).join(", ") +
    (pd.zip ? ` ${pd.zip}` : "");
  const status = deriveStatus(project);
  const pct = deriveProgressPct(project);
  const budget = deriveBudget(project);
  const days = daysBetween(project.start_date, project.actual_completion);

  // `in_rehab` is the "actively being rehabbed / editable" flag. Not-started
  // projects are reactivated via the inline "Start Rehab" button below;
  // completed ones are parked (read-only).
  const editable = project.in_rehab;

  // Primary inline action, status-driven (mirrors the design's `.rc-actions`):
  //   - not_started → "Start Rehab" (begins/reactivates the project)
  //   - in_progress at 100% → "Mark Complete" (flips in_rehab false; on the
  //     next render the card is parked and the button disappears).
  // `isCompleting` disables Mark Complete during the in-flight write.
  const actionButton = (() => {
    if (status === "not_started") {
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleStart();
          }}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-primary px-2.5 py-2 text-xs font-bold text-primary-foreground transition hover:brightness-110"
        >
          <Play className="h-3.5 w-3.5" aria-hidden /> Start Rehab
        </button>
      );
    }
    if (editable && status === "in_progress" && pct >= 100) {
      return (
        <button
          type="button"
          disabled={isCompleting}
          onClick={async (e) => {
            e.stopPropagation();
            setIsCompleting(true);
            try {
              await onComplete(project.id);
            } finally {
              setIsCompleting(false);
            }
          }}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-success px-2.5 py-2 text-xs font-bold text-success-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Check className="h-3.5 w-3.5" aria-hidden />{" "}
          {isCompleting ? "Completing…" : "Mark Complete"}
        </button>
      );
    }
    return null;
  })();

  return (
    <>
    <Card
      onClick={() => onOpen(project)}
      className={cn(
        "relative flex cursor-pointer flex-col overflow-hidden p-0 transition",
        "hover:-translate-y-0.5 hover:border-border/80 hover:shadow-md",
        // Completed (parked, moved to Properties) and not-started projects both
        // render visually muted/gray — only in-progress rehabs stay full-strength.
        (status === "completed" || status === "not_started") && "opacity-60",
      )}
    >
      {/* Head: thumbnail + address + status */}
      <div className="flex items-center gap-3 p-3.5">
        <div
          className="grid h-[72px] w-24 flex-shrink-0 place-items-center overflow-hidden rounded-lg border border-border bg-muted/40 bg-cover bg-center"
          style={pd.imageUrl ? { backgroundImage: `url('${pd.imageUrl}')` } : undefined}
          aria-hidden
        >
          {!pd.imageUrl && <Home className="h-5 w-5 text-primary" />}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="truncate text-sm font-extrabold text-foreground">
            {addr1}
          </div>
          {addr2 && (
            <div className="truncate text-xs text-muted-foreground">{addr2}</div>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10.5px] font-bold",
                STATUS_STYLES[status],
              )}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
              {STATUS_LABEL[status]}
            </span>
            {project.strategy && <StrategyBadge strategy={project.strategy} />}
          </div>
        </div>

      </div>

      {/* Body */}
      <div className="flex flex-col gap-3 px-3.5 pb-3.5">
        {/* Two tiles: Progress + Days in Rehab */}
        <div className="grid grid-cols-2 gap-2.5">
          <div className="flex flex-col gap-2 rounded-lg border border-border bg-muted/40 p-2.5">
            <span className="text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">
              Progress
            </span>
            <span className="text-xl font-extrabold leading-none tabular-nums text-foreground">
              {pct}%
            </span>
            <div className="h-1.5 w-full overflow-hidden rounded-full border border-border bg-card">
              <div
                className={cn(
                  "h-full rounded-full transition-all",
                  status === "completed"
                    ? "bg-gradient-to-r from-success to-success/60"
                    : "bg-gradient-to-r from-primary to-primary/60",
                )}
                style={{ width: `${pct}%` }}
                aria-hidden
              />
            </div>
          </div>
          <div className="flex flex-col gap-2 rounded-lg border border-border bg-muted/40 p-2.5">
            <span className="text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">
              Days in Rehab
            </span>
            <span className="text-xl font-extrabold leading-none tabular-nums text-foreground">
              {days}
              <span className="ml-1 text-xs font-semibold text-muted-foreground">
                {days === 1 ? "day" : "days"}
                {project.actual_completion ? " · done" : ""}
              </span>
            </span>
          </div>
        </div>

        {/* Budget card */}
        <div className="flex flex-col gap-2 rounded-lg border border-border bg-muted/40 p-3">
          <div className="flex items-center justify-between">
            <span className="text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">
              Budget Used
            </span>
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-[10.5px] font-bold",
                budget.overBudget
                  ? "bg-destructive/15 text-destructive"
                  : "bg-success/15 text-success",
              )}
            >
              {budget.pct}% · {budget.overBudget ? "over" : "on track"}
            </span>
          </div>
          <div className="flex items-baseline justify-between gap-2">
            <span
              className={cn(
                "text-xl font-extrabold leading-none tabular-nums",
                budget.overBudget ? "text-destructive" : "text-foreground",
              )}
            >
              {formatCurrency(budget.used)}
            </span>
            <span className="text-xs font-semibold text-muted-foreground">
              of {formatCurrency(budget.budget)}
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full border border-border bg-card">
            <div
              className={cn(
                "h-full rounded-full transition-all",
                budget.overBudget
                  ? "bg-gradient-to-r from-destructive to-destructive/60"
                  : "bg-gradient-to-r from-success to-success/60",
              )}
              style={{ width: `${Math.min(100, budget.pct)}%` }}
              aria-hidden
            />
          </div>
          <div className="flex items-baseline justify-between text-[11px] tabular-nums">
            <span
              className={cn(
                "font-bold",
                budget.overBudget ? "text-destructive" : "text-success",
              )}
            >
              {budget.overBudget
                ? `${formatCurrency(budget.used - budget.budget)} over budget`
                : `${formatCurrency(Math.max(0, budget.budget - budget.used))} remaining`}
            </span>
            <span className="text-muted-foreground">
              Cap at {formatCurrency(budget.budget)}
            </span>
          </div>
        </div>

        {/* Inline action (status-dependent) */}
        {actionButton && <div className="flex gap-2">{actionButton}</div>}

        {/* Open detail */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpen(project);
          }}
          className="inline-flex w-full items-center justify-between gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-xs font-bold text-foreground transition hover:bg-card hover:border-border/80"
        >
          <span className="inline-flex items-center gap-2">
            <Pencil className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
            Update progress, budget &amp; tasks
          </span>
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        </button>
      </div>
    </Card>

    <AlertDialog open={rentedBlockOpen} onOpenChange={setRentedBlockOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>This property is currently rented</AlertDialogTitle>
          <AlertDialogDescription>
            You can't start a rehab while the unit has an active tenant.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li>
            <span className="font-semibold text-foreground">To rehab it:</span>{" "}
            first remove the property from rental on the Rentals page.
          </li>
          <li>
            <span className="font-semibold text-foreground">
              For minor repairs or upkeep:
            </span>{" "}
            use the Property Management section instead — no need to end the
            tenancy.
          </li>
        </ul>
        <AlertDialogFooter>
          <AlertDialogAction>Got it</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
